import {
  autoUpdate,
  flip,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { MouseEvent, TouchEvent, useRef, useState } from "react";
import type { Range } from "slate";
import { Editor, Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";
import { twMerge } from "tailwind-merge";

import { Popup } from "./popups";
import { Button, ButtonProps } from "../../button";
import { ContextMenu } from "../../context-menu";
import { Select } from "../../input";
import { Tooltip } from "../../tooltip";
import {
  blockFormats,
  BlockFormatOption,
  FeatureOptions,
  getParagraphTag,
  hasMixedParagraphStyles,
  hotkeyLabel,
  indentFormats,
  isBlockActive,
  isMarkActive,
  markOptions,
  marks,
  modalOptions,
  modals,
  resolveFeatureOptions,
  setParagraphTag,
  TextStyleTag,
  textStyleOptions,
  toggleMark,
  toTextStyleTag,
  useBuildPlaceholderMenu,
  useEditorContext,
} from "../hooks";

const toolbarWrapperStyles =
  "p-4 flex flex-row flex-wrap gap-3 border-b border-neutral-100 mx-4";
const popupStyles = "bg-white rounded shadow-dropdownMenu z-1 text-ui";

const blockFormatOrder: BlockFormatOption[] = [
  "blockquote",
  "numberedList",
  "bulletedList",
];

export const Toolbar = ({
  className,
  rightToolbarButtons,
  featureOptions,
}: {
  className?: string;
  rightToolbarButtons?: ButtonProps[];
  featureOptions?: FeatureOptions;
}) => {
  const editor = useSlate();
  const { modal, setModal } = useEditorContext();
  const placeholderMenu = useBuildPlaceholderMenu();
  const [placeholderMenuVisible, setPlaceholderMenuVisible] = useState(false);
  const features = resolveFeatureOptions(featureOptions);

  const popup = useFloating({
    placement: "bottom-start",
    open: !!modal,
    onOpenChange: () => setModal(undefined),
    middleware: [
      shift({ padding: 10 }),
      flip({ fallbackPlacements: ["bottom", "top"] }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const popupInteractions = useInteractions([useDismiss(popup.context)]);

  // keep last real selection, editor loses it when Select takes focus
  const lastSelectionRef = useRef<Range | null>(null);
  if (editor.selection) lastSelectionRef.current = editor.selection;

  // fallback to document start so dropdown targets the first block
  const documentStartRange = (): Range | null => {
    if (editor.children.length === 0) return null;
    const start = Editor.start(editor, []);
    return { anchor: start, focus: start };
  };
  const styleSelection =
    editor.selection ?? lastSelectionRef.current ?? documentStartRange();

  const handleTextStyleChange = (value: TextStyleTag) => {
    if (!styleSelection) return;
    // apply tag before reselecting, else onValueChange is skipped
    setParagraphTag(editor, value, styleSelection)();
    Transforms.select(editor, styleSelection);
    ReactEditor.focus(editor);
  };

  const applyIndentFormat =
    (option: keyof typeof indentFormats) => (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      indentFormats[option].apply(editor);
      ReactEditor.focus(editor);
    };

  return (
    <div className={twMerge(toolbarWrapperStyles, className)}>
      {features.textStyle && (
        <Tooltip
          id="toolbar-text-style"
          placement="top"
          text="Text Style"
          offsetBy={5}
          variant="paragraphy"
        >
          <Select
            value={
              hasMixedParagraphStyles(editor, styleSelection)
                ? undefined
                : toTextStyleTag(getParagraphTag(editor, styleSelection))
            }
            onChange={handleTextStyleChange}
            options={textStyleOptions}
            dataTestId="toolbar-text-style"
            className="w-[12rem]"
            compact
          />
        </Tooltip>
      )}
      {markOptions
        .filter((option) => features[option])
        .map((option) => (
          <Tooltip
            id={option}
            key={option}
            placement="top"
            text={`${marks[option].text ? `${marks[option].text}\n` : ""}${hotkeyLabel(marks[option].hotkey)}`}
            offsetBy={5}
            variant="paragraphy"
          >
            <Button
              variant="inlineSubtle"
              leftIcon={marks[option].icon}
              dataTestId={`toolbar-${option}`}
              isPressed={isMarkActive(editor, option)}
              onMouseDown={toggleMark(editor, option)}
              onTouchStart={toggleMark(editor, option)}
            />
          </Tooltip>
        ))}
      {modalOptions
        .filter((option) => features[option])
        .map((option) => (
          <Tooltip
            id={option}
            key={option}
            placement="top"
            text={
              modal?.option === option ||
              isBlockActive(editor, ["link", "placeholder"])
                ? undefined
                : `${modals[option].text ? `${modals[option].text}\n` : ""}${hotkeyLabel(modals[option].hotkey)}`
            }
            offsetBy={5}
            variant="paragraphy"
          >
            <div
              ref={
                modal?.option === option ? popup.refs.setReference : undefined
              }
              {...popupInteractions.getReferenceProps()}
            >
              <Button
                variant="inlineSubtle"
                leftIcon={
                  modal?.option === option
                    ? modals[option].pressedIcon
                    : modals[option].icon
                }
                dataTestId={`toolbar-${option}`}
                disabled={isBlockActive(editor, ["link", "placeholder"])}
                onMouseDown={() => setModal({ option })}
                onTouchStart={() => setModal({ option })}
              />
            </div>
          </Tooltip>
        ))}
      {blockFormatOrder
        .filter((option) => features[blockFormats[option].feature])
        .map((option) => (
          <Tooltip
            id={option}
            key={option}
            placement="top"
            text={blockFormats[option].text}
            offsetBy={5}
            variant="paragraphy"
          >
            <Button
              variant="inlineSubtle"
              leftIcon={blockFormats[option].icon}
              dataTestId={`toolbar-${option}`}
              isPressed={isBlockActive(
                editor,
                blockFormats[option].activeTypes,
              )}
              onMouseDown={blockFormats[option].toggle(editor)}
              onTouchStart={blockFormats[option].toggle(editor)}
            />
          </Tooltip>
        ))}
      {features.indent &&
        (["outdent", "indent"] as const).map((option) => (
          <Tooltip
            id={option}
            key={option}
            placement="top"
            text={indentFormats[option].text}
            offsetBy={5}
            variant="paragraphy"
          >
            <Button
              variant="inlineSubtle"
              leftIcon={indentFormats[option].icon}
              dataTestId={`toolbar-${option}`}
              onMouseDown={applyIndentFormat(option)}
              onTouchStart={applyIndentFormat(option)}
            />
          </Tooltip>
        ))}
      {placeholderMenu && (
        <Tooltip
          id={placeholderMenu.id}
          placement="top"
          text={
            isBlockActive(editor, ["link", "placeholder"]) ||
            placeholderMenuVisible
              ? undefined
              : `${placeholderMenu.text ?? ""}\n${hotkeyLabel(placeholderMenu.hotkey) ?? ""}`.trim()
          }
          offsetBy={5}
          variant="paragraphy"
        >
          <ContextMenu
            id="placeholder"
            button={{
              variant: "inlineSubtle",
              leftIcon: placeholderMenuVisible
                ? placeholderMenu.pressedIcon
                : placeholderMenu.icon,
              disabled: isBlockActive(editor, ["link", "placeholder"]),
            }}
            items={placeholderMenu.items}
            placement="bottom-start"
            offsetBy={4}
            onToggle={(open) => setPlaceholderMenuVisible(open)}
          />
        </Tooltip>
      )}
      {rightToolbarButtons && (
        <div className="flex flex-1 justify-end">
          {rightToolbarButtons.map((button) => (
            <Button key={button.text} {...button} />
          ))}
        </div>
      )}
      {!!modal && (
        <span
          className={popupStyles}
          ref={popup.refs.setFloating}
          style={popup.floatingStyles}
          {...popupInteractions.getFloatingProps()}
        >
          <Popup {...modal} />
        </span>
      )}
    </div>
  );
};
